// Field validation utilities for PDF generation phase
import { UIFieldCoordinates } from '../types/codeGeneration';
import { characterBlockGroupErrors } from './characterBlockGroups';

export interface FieldValidationResult {
  isValid: boolean;
  invalidFields: {
    fieldId: string;
    fieldName: string;
    errors: string[];
  }[];
  errorSummary: string;
}

export interface FieldValidationError {
  fieldId: string;
  field: 'name' | 'type' | 'dimensions';
  message: string;
}

/**
 * The PDF field name a given author-facing name becomes.
 *
 * MIRRORS `PDFGenerationService.sanitizeFieldNameForPDF` EXACTLY. If that method changes,
 * this must change with it — the two are compared against each other by
 * `fieldValidation.duplicate.test.ts`, which is the only thing stopping them drifting.
 *
 * <p>The collapsing is aggressive and that is the whole reason this function exists:
 * `First Name`, `First-Name`, `FIRST NAME` and `first.name` all become `first_name`.
 */
export const pdfFieldName = (name: string): string =>
  name
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

/**
 * The other field whose name would collide with this one in the generated PDF, or null.
 *
 * WHY THIS MATTERS MORE THAN IT LOOKS. ISO 32000-1 clause 12.7.3.2 does not treat two
 * fields sharing a name as a collision to resolve — it treats them as ONE FIELD with two
 * widgets. They fill in unison: type in the first and the second shows the same text. The
 * file validates green, veraPDF has no rule for it, and the author discovers it when a
 * citizen's answers appear in the wrong box.
 *
 * Duplicates also make a generated Spring Boot project FAIL TO COMPILE, because two fields
 * produce the same Java property.
 *
 * <p>Comparison is on the SANITISED name, not the typed one. Two visibly different names
 * can still collide — that is the case an author cannot see and most needs telling about.
 *
 * @param fields every field on the document, including the one being checked
 * @param field  the field whose name is being validated
 */
export const findFieldNameCollision = (
  fields: { id: string; name?: string }[],
  field: { id: string; name?: string }
): { id: string; name?: string } | null => {
  const raw = (field.name || '').trim();
  // An empty name is already reported as "required"; the backend gives it a unique
  // generated name, so it cannot collide and a second error would just be noise.
  if (!raw || raw === 'loading...') return null;

  const mine = pdfFieldName(raw);
  if (!mine) return null;

  return fields.find(
    (other) => other.id !== field.id && pdfFieldName((other.name || '').trim()) === mine
  ) ?? null;
};

/**
 * Validates fields for PDF generation readiness
 * This is the final validation before proceeding to generation phase
 */
export const validateFieldsForGeneration = (fields: UIFieldCoordinates[]): FieldValidationResult => {
  const invalidFields: { fieldId: string; fieldName: string; errors: string[] }[] = [];
  
  fields.forEach((field, index) => {
    const errors: string[] = [];
    const displayName = getFieldDisplayName(field, index);
    
    // Check for missing or invalid field name
    if (!field.name || field.name.trim() === '') {
      errors.push('Field name is required');
    } else if (field.name === 'loading...') {
      errors.push('Field name is still loading');
    } else {
      // NO WHITESPACE — owner ruling, 6 Oct 2026: "I was able to save a field
      // name with a space between the letters, that is bad." Spaces were
      // historically tolerated here and silently rewritten to underscores at
      // generation, which meant the name the author saw and the identity the
      // PDF carried were two spellings of one fact — the exact ambiguity the
      // Name/Label split exists to prevent. The Name is machine identity;
      // human wording belongs in the Label. Hyphen and underscore stay legal:
      // they are common identifier conventions and the collision check already
      // catches the sanitise-equal cases.
      if (!/^[a-zA-Z0-9_-]+$/.test(field.name)) {
        errors.push('Field name may use only letters, numbers, underscore and hyphen — no spaces. Human wording belongs in the Label.');
      }
      
      // Check for reserved field names
      const reservedNames = ['loading...', 'undefined', 'null', 'NaN'];
      if (reservedNames.includes(field.name.toLowerCase())) {
        errors.push('Field name is reserved');
      }
    }
    
    // Check for missing field type
    if (!field.type) {
      errors.push('Field type is required');
    }
    
    // Check for invalid dimensions
    if (!field.width || field.width <= 0) {
      errors.push('Field width must be greater than 0');
    }
    
    if (!field.height || field.height <= 0) {
      errors.push('Field height must be greater than 0');
    }
    
    // Check for negative positions (warning, not error)
    if (field.x < 0 || field.y < 0) {
      errors.push('Field position may be outside visible area');
    }
    
    // Character block specific validation
    if (field.type === 'character_block') {
      if (!field.characterBlockConfig) {
        errors.push('Character block configuration is missing');
      } else {
        if (!field.characterBlockConfig.totalBlocks || field.characterBlockConfig.totalBlocks <= 0) {
          errors.push('Character block must have at least 1 block');
        }
        if (field.characterBlockConfig.blockSpacing < 0) {
          errors.push('Character block spacing cannot be negative');
        }
        // Each group of boxes becomes its own comb field and needs its own accessible
        // name, so an unnamed group or a group list that disagrees with the block size
        // blocks generation (CHARACTER_BLOCK_GROUP_LABELS_SPEC.md section 5).
        characterBlockGroupErrors(field.characterBlockConfig).forEach(message => errors.push(message));
      }
    }
    
    // Image region specific validation.
    //
    // THE REFUSAL, AND IT BLOCKS GENERATION. Every other missing accessibility value in this
    // product produces a worse file; this one produces an INVALID file — a Figure with an
    // empty /Alt fails veraPDF rule 7.3-1 outright. The backend refuses it too, and says the
    // same thing; this is the copy the author sees while the mistake is still cheap.
    //
    // There is deliberately no fabricated default anywhere in this path: not the field name,
    // not the filename, not the page text beside the image. A plausible wrong description is
    // worse than a missing one, because the author stops reading it and nothing downstream
    // flags it.
    if (field.type === 'image_region') {
      const purpose = field.imageRegionConfig?.purpose;
      if (!purpose) {
        errors.push('Say what this image does: shows something, spells out words, or is decoration');
      } else if (purpose !== 'decoration' && !(field.imageRegionConfig?.text || '').trim()) {
        // "Decoration" is the honest answer to "this image says nothing", and it is offered
        // in the same breath as the refusal — otherwise the refusal is a dead end and the
        // author types a space to get past it, which passes nothing.
        errors.push(
          purpose === 'replace'
            ? 'Type the words in this picture, or choose “It is decoration”'
            : 'Describe what this image shows, or choose “It is decoration”'
        );
      }
    }

    // Paragraph region: a role and the words, both the author's, neither derived. The role
    // (body text vs a heading level) is a Matterhorn Human judgement the tool never makes; the
    // text tags body content a reader would otherwise never hear. An empty region tags nothing
    // — the untagged graphic it was drawn to fix — and the backend refuses it, so this is the
    // copy the author sees while the mistake is still cheap. There is no "mark it decoration"
    // escape here: a paragraph with no words is not a real answer the way a decorative image is.
    if (field.type === 'paragraph') {
      if (!field.paragraphConfig?.role) {
        errors.push('Say what this text is: body text, or a heading level');
      }
      if (!(field.paragraphConfig?.text || '').trim()) {
        errors.push('Type the words this region covers');
      }
    }

    // Radio group specific validation
    if (field.type === 'radio_group') {
      if (!field.radioGroupConfig) {
        errors.push('Radio button configuration is missing');
      } else {
        // Note: groupName is not required as radio groups use the field name
        if (!field.radioGroupConfig.options || field.radioGroupConfig.options.length === 0) {
          errors.push('Radio group must have at least one option');
        }
      }
    }
    
    if (errors.length > 0) {
      invalidFields.push({
        fieldId: field.id,
        fieldName: displayName,
        errors
      });
    }
  });
  
  const isValid = invalidFields.length === 0;
  const errorSummary = isValid 
    ? 'All fields are valid' 
    : `${invalidFields.length} field${invalidFields.length !== 1 ? 's' : ''} ${invalidFields.length !== 1 ? 'have' : 'has'} validation errors`;
  
  return {
    isValid,
    invalidFields,
    errorSummary
  };
};

/**
 * Gets display name for a field (same logic as in components)
 */
export const getFieldDisplayName = (field: UIFieldCoordinates, fallbackIndex?: number): string => {
  // If field has a valid name that's not loading or empty
  if (field.name && field.name.trim() !== '' && field.name !== 'loading...') {
    return field.name;
  }
  
  // Extract numeric part from field ID (e.g., "field_123" -> "123")
  const idMatch = field.id.match(/\d+/);
  const fieldNumber = idMatch ? idMatch[0] : (fallbackIndex ?? 0) + 1;
  
  // Return field ID as display name
  return `Field_${fieldNumber}`;
};

/**
 * Checks if a specific field has validation errors
 */
export const getFieldValidationErrors = (field: UIFieldCoordinates, index?: number): string[] => {
  const result = validateFieldsForGeneration([field]);
  return result.invalidFields.length > 0 ? result.invalidFields[0].errors : [];
};

/**
 * Checks if a field name is valid
 */
export const isValidFieldName = (name: string): boolean => {
  if (!name || name.trim() === '' || name === 'loading...') {
    return false;
  }
  
  // No whitespace — same rule and same reasoning as validateFieldsForGeneration
  // above (owner ruling, 6 Oct 2026). These two regexes MUST stay identical;
  // they drifted apart once and the drift was this defect.
  if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
    return false;
  }
  
  // Check for reserved names
  const reservedNames = ['loading...', 'undefined', 'null', 'NaN'];
  if (reservedNames.includes(name.toLowerCase())) {
    return false;
  }
  
  return true;
};